import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Check, X, Copy, Inbox } from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import api, { apiError } from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const FILTERS = [{ v: "pending", t: "Pendientes" }, { v: "approved", t: "Aprobadas" }, { v: "rejected", t: "Rechazadas" }, { v: "", t: "Todas" }];
const BADGE = { pending: { t: "Pendiente", c: "st-recibido" }, approved: { t: "Aprobada", c: "st-terminado" }, rejected: { t: "Rechazada", c: "st-analisis" } };

export default function Solicitudes() {
  const [tab, setTab] = useState("work");
  const [workRows, setWorkRows] = useState(null);
  const [proposal, setProposal] = useState({});
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setRows(null);
    api.get("/admin/pilot-requests", { params: filter ? { status: filter } : {} })
      .then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  const loadWork = useCallback(() => api.get("/admin/work-requests").then(({data}) => setWorkRows(data)).catch(() => setWorkRows([])), []);
  useEffect(() => { loadWork(); }, [loadWork]);
  const sendProposal = async (id) => {
    const p = proposal[id] || {};
    try { await api.post(`/admin/work-requests/${id}/proposal`, p); toast.success("Propuesta enviada al despacho."); loadWork(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const approve = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/pilot-requests/${id}/approve`);
      toast.success("Solicitud aprobada. Invitación enviada.");
      if (data.invitation_link) {
        navigator.clipboard?.writeText(data.invitation_link).catch(() => {});
        toast.message("Enlace de invitación copiado al portapapeles.");
      }
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };

  const reject = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/pilot-requests/${id}/reject`);
      toast.success("Solicitud rechazada.");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };

  return (
    <AppLayout variant="admin">
      <div className="ddp-fade">
        <h1 className="ddp-serif" style={{ fontSize: "clamp(28px,4vw,40px)", color: "var(--burgundy-dark)", margin: 0, letterSpacing: "-.02em" }}>Solicitudes</h1>
        <p style={{ color: "var(--muted)", fontSize: 15, marginTop: 8 }}>Revise nuevos trabajos y gestione el acceso de los despachos.</p>
      </div>

      <div style={{display:"flex",gap:8,marginTop:22}}><button className={`ddp-btn ddp-btn-sm ${tab==="work"?"ddp-btn-primary":"ddp-btn-ghost"}`} onClick={()=>setTab("work")}>Trabajos planteados</button><button className={`ddp-btn ddp-btn-sm ${tab==="access"?"ddp-btn-primary":"ddp-btn-ghost"}`} onClick={()=>setTab("access")}>Acceso de despachos</button></div>

      {tab === "work" && <div style={{display:"grid",gap:14,marginTop:18}}>{workRows===null?<div>Cargando…</div>:workRows.length===0?<div className="ddp-card" style={{padding:36,color:"var(--muted)"}}>No hay trabajos planteados.</div>:workRows.map(r=><div key={r.id} className="ddp-card" style={{padding:22}}><div style={{display:"flex",justifyContent:"space-between",gap:15,flexWrap:"wrap"}}><div><strong>{r.internal_name}</strong><div style={{fontSize:13,color:"var(--muted)",marginTop:4}}>{r.firm_name} · {fmtDate(r.created_at)} · {r.status}</div></div></div><div style={{display:"grid",gap:8,marginTop:15,fontSize:14}}><div><b>Contexto:</b> {r.context}</div><div><b>Trabajo solicitado:</b> {r.requested_work}</div></div>{["pending_review","proposal_sent"].includes(r.status)&&<div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:10,marginTop:16}}><input className="ddp-input" placeholder="Alcance del trabajo" value={proposal[r.id]?.scope||""} onChange={e=>setProposal({...proposal,[r.id]:{...proposal[r.id],scope:e.target.value}})}/><input className="ddp-input" placeholder="Precio" value={proposal[r.id]?.price||""} onChange={e=>setProposal({...proposal,[r.id]:{...proposal[r.id],price:e.target.value}})}/><input className="ddp-input" placeholder="Entrega" value={proposal[r.id]?.delivery||""} onChange={e=>setProposal({...proposal,[r.id]:{...proposal[r.id],delivery:e.target.value}})}/><textarea className="ddp-textarea" style={{gridColumn:"1 / -1"}} rows="2" placeholder="Observaciones (opcional)" value={proposal[r.id]?.observations||""} onChange={e=>setProposal({...proposal,[r.id]:{...proposal[r.id],observations:e.target.value}})}/><button className="ddp-btn ddp-btn-primary ddp-btn-sm" style={{justifySelf:"start"}} onClick={()=>sendProposal(r.id)}>Enviar propuesta</button></div>}</div>)}</div>}

      {tab === "access" && <>
      <div style={{ display: "flex", gap: 8, marginTop: 22, flexWrap: "wrap" }}>
        {FILTERS.map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`filter-${f.v || "all"}`}
            className={`ddp-btn ddp-btn-sm ${filter === f.v ? "ddp-btn-primary" : "ddp-btn-ghost"}`}>{f.t}</button>
        ))}
      </div>

      <div className="ddp-card" style={{ marginTop: 18, overflow: "hidden" }}>
        {rows === null ? <div style={{ padding: 40, color: "var(--muted)" }}>Cargando…</div>
          : rows.length === 0 ? (
            <div style={{ padding: "56px 24px", textAlign: "center" }}>
              <Inbox size={38} style={{ color: "var(--line)", margin: "0 auto 14px" }} />
              <div style={{ color: "var(--muted)" }}>No hay solicitudes en este estado.</div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="ddp-table" data-testid="requests-table">
                <thead><tr><th>Despacho</th><th>Solicitante</th><th>Email</th><th>Fecha</th><th>Estado</th><th></th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} style={{ cursor: "default" }} data-testid={`request-row-${r.email}`}>
                      <td style={{ fontWeight: 600 }}>{r.firm_name}<div style={{ fontSize: 11, fontWeight: 700, color: "var(--burgundy-med)", marginTop: 3 }}>{r.request_type === "closed" ? "Caso cerrado" : "Asunto en curso"}</div></td>
                      <td>{r.name} {r.surname}</td>
                      <td style={{ color: "var(--muted)", maxWidth: 220, overflowWrap: "anywhere", wordBreak: "break-word" }}>{r.email}</td>
                      <td style={{ color: "var(--muted)" }}>{fmtDate(r.created_at)}</td>
                      <td><span className={`ddp-status ${BADGE[r.status]?.c}`}><span className="dot" />{BADGE[r.status]?.t}</span></td>
                      <td>
                        {r.status === "pending" && (
                          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={() => approve(r.id)} disabled={busy === r.id} data-testid={`approve-${r.email}`} className="ddp-btn ddp-btn-primary ddp-btn-sm"><Check size={15} /> Aprobar</button>
                            <button onClick={() => reject(r.id)} disabled={busy === r.id} data-testid={`reject-${r.email}`} className="ddp-btn ddp-btn-ghost ddp-btn-sm"><X size={15} /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
      </>}
    </AppLayout>
  );
}
